import { ref, computed } from 'vue'
import { useGlobalState } from './useGlobalState.js'

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export interface BudgetCategoryData {
  id: number
  name: string
  type: 'income' | 'expense'
  accountType: 'personal' | 'professional'
  formula: string | null
  sortOrder: number
  isTaxable: boolean
  isTnsRemuneration: boolean
  isUrssafPayment: boolean
  isTaxPayment: boolean
  isCorporateTaxPayment: boolean
  isNonEditable: boolean
  isDebt: boolean
  isParameterSum: boolean
  corporateTaxDirection: 'income' | 'expense' | null
  parentId: number | null
  months: Record<number, number | null>
  monthOverrides: Record<number, boolean>
}

export interface BudgetOpeningBalanceData {
  amount: number | null
  isManual: boolean
}

export interface BudgetAccountData {
  categories: BudgetCategoryData[]
  openingBalances: Record<number, BudgetOpeningBalanceData>
}

export interface BudgetYearData {
  personal: BudgetAccountData
  professional: BudgetAccountData
}

export interface BudgetCategoryPayload {
  name: string
  accountType: 'personal' | 'professional'
  type: 'income' | 'expense' | 'parameter'
  formula?: string | null
  sortOrder?: number
  isTaxable?: boolean
  isTnsRemuneration?: boolean
  isUrssafPayment?: boolean
  isTaxPayment?: boolean
  isCorporateTaxPayment?: boolean
  isNonEditable?: boolean
  isDebt?: boolean
  isParameterSum?: boolean
  corporateTaxDirection?: 'income' | 'expense' | null
  parentId?: number | null
}

export interface BudgetSnapshot {
  id: number
  name: string | null
  isAuto: boolean
  createdAt: string
  updatedAt: string
}

// ─────────────────────────────────────────────────────────────────────────────
// Singleton state
// ─────────────────────────────────────────────────────────────────────────────

const currentYear = ref<number>(new Date().getFullYear())
const budgetDataByYear = ref<Record<number, BudgetYearData>>({})
const taxStrategies = ref<Record<number, any>>({})
const loading     = ref(false)
const error       = ref<string | null>(null)

const isReadOnlyMode = ref(false)
const currentSnapshot = ref<BudgetSnapshot | null>(null)
const snapshots = ref<BudgetSnapshot[]>([])

// ─────────────────────────────────────────────────────────────────────────────
// Composable
// ─────────────────────────────────────────────────────────────────────────────

export function useBudget() {
  const { getAuthHeader } = useGlobalState()

  // ── Computed ───────────────────────────────────────────────────────────────

  const budgetData        = computed(() => budgetDataByYear.value[currentYear.value] ?? null)
  const personalData      = computed(() => budgetData.value?.personal ?? null)
  const professionalData  = computed(() => budgetData.value?.professional ?? null)

  // ── Helpers ────────────────────────────────────────────────────────────────

  function buildHeaders(): HeadersInit {
    return {
      'Content-Type': 'application/json',
      ...getAuthHeader(),
    }
  }

  async function handleResponse(res: Response): Promise<unknown> {
    const json = await res.json()
    if (!res.ok) throw new Error((json as { error?: string }).error ?? `HTTP ${res.status}`)
    return json
  }

  // ── Fetch year ─────────────────────────────────────────────────────────────

  /**
   * JSON serialization turns numeric object keys into strings (e.g. {"1": ..., "2": ...}).
   * This helper normalizes them back to number-keyed Records so the rest of the code
   * can reliably use months[1], monthOverrides[1], openingBalances[1], etc.
   */
  function normalizeAccountData(account: BudgetAccountData): BudgetAccountData {
    return {
      ...account,
      categories: account.categories.map(cat => ({
        ...cat,
        months: Object.fromEntries(
          Object.entries(cat.months).map(([k, v]) => [parseInt(k, 10), v !== null ? Number(v) : null])
        ) as Record<number, number | null>,
        monthOverrides: Object.fromEntries(
          Object.entries(cat.monthOverrides).map(([k, v]) => [parseInt(k, 10), Boolean(v)])
        ) as Record<number, boolean>,
      })),
      openingBalances: Object.fromEntries(
        Object.entries(account.openingBalances).map(([k, v]) => [
          parseInt(k, 10),
          { amount: v.amount !== null ? Number(v.amount) : null, isManual: Boolean(v.isManual) },
        ])
      ) as Record<number, BudgetOpeningBalanceData>,
    }
  }

  async function fetchYear(year: number): Promise<void> {
    loading.value = true
    error.value   = null
    try {
      if (!budgetDataByYear.value[year]) {
        const res  = await fetch(`/api/budget/${year}`, { headers: getAuthHeader() })
        const data = await handleResponse(res) as { personal: BudgetAccountData; professional: BudgetAccountData; taxStrategies?: Record<number, any> }
        budgetDataByYear.value[year] = {
          personal:     normalizeAccountData(data.personal),
          professional: normalizeAccountData(data.professional),
        }
        if (data.taxStrategies) {
          taxStrategies.value = data.taxStrategies
        }
      }

      // Proactively fetch previous year to allow formulas to look back across years
      const prevYear = year - 1
      if (!budgetDataByYear.value[prevYear]) {
        const resPrev  = await fetch(`/api/budget/${prevYear}`, { headers: getAuthHeader() })
        const dataPrev = await handleResponse(resPrev) as { personal: BudgetAccountData; professional: BudgetAccountData; taxStrategies?: Record<number, any> }
        budgetDataByYear.value[prevYear] = {
          personal:     normalizeAccountData(dataPrev.personal),
          professional: normalizeAccountData(dataPrev.professional),
        }
        if (dataPrev.taxStrategies) {
          taxStrategies.value = dataPrev.taxStrategies
        }
      }

      currentYear.value = year
    } catch (e: unknown) {
      error.value = e instanceof Error ? e.message : 'Erreur de chargement'
    } finally {
      loading.value = false
    }
  }

  // ── Snapshots ──────────────────────────────────────────────────────────────

  async function fetchSnapshots(): Promise<void> {
    const res = await fetch(`/api/budget/snapshots`, { headers: getAuthHeader() })
    snapshots.value = await handleResponse(res) as BudgetSnapshot[]
  }

  async function createManualSnapshot(name: string): Promise<void> {
    const res = await fetch(`/api/budget/snapshots`, {
      method: 'POST',
      headers: buildHeaders(),
      body: JSON.stringify({ name }),
    })
    const snapshot = await handleResponse(res) as BudgetSnapshot
    snapshots.value.unshift(snapshot)
  }

  async function loadSnapshot(id: number): Promise<void> {
    loading.value = true
    try {
      const res = await fetch(`/api/budget/snapshots/data/${id}`, { headers: getAuthHeader() })
      const data = await handleResponse(res) as { id: number, name: string | null, isAuto: boolean, data: any }
      
      // We completely replace the budget workspace with the snapshot data
      const newWorkspace: Record<number, BudgetYearData> = {}
      for (const yearStr in data.data) {
        const y = parseInt(yearStr, 10)
        newWorkspace[y] = {
          personal: normalizeAccountData(data.data[yearStr].personal),
          professional: normalizeAccountData(data.data[yearStr].professional),
        }
      }
      budgetDataByYear.value = newWorkspace
      
      const snapObj = snapshots.value.find(s => s.id === id)
      currentSnapshot.value = snapObj || { id: data.id, name: data.name, isAuto: data.isAuto, createdAt: '', updatedAt: '' }
      isReadOnlyMode.value = true
      // Note: we don't change currentYear.value, it stays on whatever the user was viewing
    } catch (e: unknown) {
      error.value = e instanceof Error ? e.message : 'Erreur de chargement du snapshot'
    } finally {
      loading.value = false
    }
  }

  async function exitReadOnlyMode(): Promise<void> {
    isReadOnlyMode.value = false
    currentSnapshot.value = null
    
    // Clear everything and re-fetch the current year (which will also fetch prev year)
    budgetDataByYear.value = {}
    await fetchYear(currentYear.value)
  }


  // ── Line upsert / delete ───────────────────────────────────────────────────

  async function upsertLine(
    categoryId: number,
    year: number,
    month: number,
    amount: number,
    isManualOverride = true,
  ): Promise<void> {
    if (isReadOnlyMode.value) throw new Error("Mode lecture seule actif")

    // Optimistic update
    _setLineValue(categoryId, year, month, amount, isManualOverride)

    try {
      await fetch('/api/budget/lines', {
        method:  'PUT',
        headers: buildHeaders(),
        body:    JSON.stringify({ categoryId, year, month, amount: String(amount), isManualOverride }),
      })
    } catch (e) {
      // Rollback on error
      _setLineValue(categoryId, year, month, null, false)
      throw e
    }
  }

  async function deleteLine(categoryId: number, year: number, month: number): Promise<void> {
    if (isReadOnlyMode.value) throw new Error("Mode lecture seule actif")

    // Optimistic update
    _setLineValue(categoryId, year, month, null, false)

    await fetch(`/api/budget/lines/${categoryId}/${year}/${month}`, {
      method:  'DELETE',
      headers: getAuthHeader(),
    })
  }

  // ── Opening balance upsert / delete ────────────────────────────────────────

  async function upsertOpeningBalance(
    accountType: 'personal' | 'professional',
    year: number,
    month: number,
    amount: number,
    isManualOverride = true,
  ): Promise<void> {
    if (isReadOnlyMode.value) throw new Error("Mode lecture seule actif")

    // Optimistic update
    _setOpeningBalance(accountType, year, month, amount, isManualOverride)

    try {
      await fetch('/api/budget/opening-balances', {
        method:  'PUT',
        headers: buildHeaders(),
        body:    JSON.stringify({ accountType, year, month, amount: String(amount), isManualOverride }),
      })
    } catch (e) {
      _setOpeningBalance(accountType, year, month, null, false)
      throw e
    }
  }

  async function deleteOpeningBalance(
    accountType: 'personal' | 'professional',
    year: number,
    month: number,
  ): Promise<void> {
    if (isReadOnlyMode.value) throw new Error("Mode lecture seule actif")

    _setOpeningBalance(accountType, year, month, null, false)

    await fetch(`/api/budget/opening-balances/${accountType}/${year}/${month}`, {
      method:  'DELETE',
      headers: getAuthHeader(),
    })
  }

  // ── Categories ─────────────────────────────────────────────────────────────

  async function createCategory(payload: BudgetCategoryPayload): Promise<BudgetCategoryData> {
    if (isReadOnlyMode.value) throw new Error("Mode lecture seule actif")

    const res  = await fetch('/api/budget/categories', {
      method:  'POST',
      headers: buildHeaders(),
      body:    JSON.stringify(payload),
    })
    const cat = await handleResponse(res) as BudgetCategoryData

    const emptyMonths: Record<number, number | null> = {}
    const emptyOverrides: Record<number, boolean>    = {}
    for (let m = 1; m <= 12; m++) {
      emptyMonths[m]    = null
      emptyOverrides[m] = false
    }

    // Insert into ALL loaded years
    for (const yearStr in budgetDataByYear.value) {
      const data = budgetDataByYear.value[yearStr]
      
      // Shift existing
      for (const c of data[payload.accountType].categories) {
        if (c.type === payload.type && c.sortOrder >= (payload.sortOrder ?? 0)) {
          c.sortOrder += 1
        }
      }

      data[payload.accountType].categories.push({ ...cat, months: { ...emptyMonths }, monthOverrides: { ...emptyOverrides } })
    }

    return { ...cat, months: emptyMonths, monthOverrides: emptyOverrides }
  }

  async function updateCategory(id: number, payload: Partial<BudgetCategoryPayload>): Promise<void> {
    if (isReadOnlyMode.value) throw new Error("Mode lecture seule actif")

    await fetch(`/api/budget/categories/${id}`, {
      method:  'PATCH',
      headers: buildHeaders(),
      body:    JSON.stringify(payload),
    })

    // Sync local state across ALL loaded years
    for (const yearStr in budgetDataByYear.value) {
      const data = budgetDataByYear.value[yearStr]
      for (const accountType of ['personal', 'professional'] as const) {
        const catList = data[accountType].categories
        const idx = catList.findIndex(c => c.id === id)
        if (idx !== -1) {
          const oldCat = catList[idx]

          if (payload.sortOrder !== undefined && payload.sortOrder !== oldCat.sortOrder) {
            for (const c of catList) {
              if (c.id !== id && c.type === oldCat.type && c.sortOrder >= payload.sortOrder) {
                c.sortOrder += 1
              }
            }
          }

          catList[idx] = { ...oldCat, ...payload }
        }
      }
    }
  }

  async function deleteCategory(id: number): Promise<void> {
    if (isReadOnlyMode.value) throw new Error("Mode lecture seule actif")

    await fetch(`/api/budget/categories/${id}`, {
      method:  'DELETE',
      headers: getAuthHeader(),
    })

    // Remove from ALL loaded years
    for (const yearStr in budgetDataByYear.value) {
      const data = budgetDataByYear.value[yearStr]
      for (const accountType of ['personal', 'professional'] as const) {
        data[accountType].categories = data[accountType].categories.filter(c => c.id !== id)
      }
    }
  }

  async function validateFormula(
    formula: string,
    categoryId: number,
  ): Promise<{ valid: boolean; error: string | null }> {
    const res = await fetch('/api/budget/categories/validate-formula', {
      method:  'POST',
      headers: buildHeaders(),
      body:    JSON.stringify({ formula, categoryId }),
    })
    return res.json()
  }

  // ── Optimistic helpers ─────────────────────────────────────────────────────

  function _setLineValue(
    categoryId: number,
    year: number,
    month: number,
    value: number | null,
    isManualOverride: boolean,
  ): void {
    const data = budgetDataByYear.value[year]
    if (!data) return
    for (const accountType of ['personal', 'professional'] as const) {
      const cat = data[accountType].categories.find(c => c.id === categoryId)
      if (cat) {
        cat.months[month]        = value
        cat.monthOverrides[month] = isManualOverride
        return
      }
    }
  }

  function _setOpeningBalance(
    accountType: 'personal' | 'professional',
    year: number,
    month: number,
    amount: number | null,
    isManual: boolean,
  ): void {
    const data = budgetDataByYear.value[year]
    if (!data) return
    data[accountType].openingBalances[month] = { amount, isManual }
  }

  return {
    // State
    currentYear,
    budgetData,
    budgetDataByYear,
    taxStrategies,
    loading,
    error,
    isReadOnlyMode,
    currentSnapshot,
    snapshots,

    // Computed
    personalData,
    professionalData,

    // Methods
    fetchYear,
    fetchSnapshots,
    createManualSnapshot,
    loadSnapshot,
    exitReadOnlyMode,
    upsertLine,
    deleteLine,
    upsertOpeningBalance,
    deleteOpeningBalance,
    createCategory,
    updateCategory,
    deleteCategory,
    validateFormula,
  }
}
